import { useEffect, useRef, useState } from "react";
import { renderAsync } from "docx-preview";
import { Loader2, AlertCircle, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

interface DocxRendererProps {
  blobData: Blob;
  filename: string;
  onDownload?: () => void;
  onLoad?: () => void;
}

/**
 * Renders .docx/.doc files inline using the docx-preview library.
 * Converts the Word document to styled HTML inside a scrollable container.
 */
export function DocxRenderer({ blobData, filename, onDownload, onLoad }: DocxRendererProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    renderAsync(blobData, container, undefined, {
      className: "docx-preview-content",
      inWrapper: true,
      ignoreWidth: false,
      ignoreHeight: true,
      ignoreFonts: false,
      breakPages: true,
      renderHeaders: true,
      renderFooters: true,
      renderFootnotes: true,
      renderEndnotes: true,
    })
      .then(() => {
        if (!cancelled) {
          setIsLoading(false);
          onLoad?.();
        }
      })
      .catch((err) => {
        if (!cancelled) {
          console.error("[DocxRenderer] Failed to render:", err);
          setError(
            "Failed to render document. The file may be corrupted or in an unsupported format."
          );
          setIsLoading(false);
        }
      });

    return () => {
      cancelled = true;
      // Clean up rendered content
      container.innerHTML = "";
    };
  }, [blobData, onLoad]);

  if (error) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 text-muted-foreground">
        <AlertCircle className="h-12 w-12 text-destructive/50" />
        <p className="max-w-md text-center text-sm">{error}</p>
        {onDownload && (
          <Button variant="outline" size="sm" onClick={onDownload}>
            <Download className="mr-2 h-4 w-4" />
            Download {filename}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="relative h-full">
      {isLoading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-background/80">
          <div className="flex flex-col items-center gap-2">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <span className="text-sm text-muted-foreground">Rendering document...</span>
          </div>
        </div>
      )}
      <div ref={containerRef} className="h-full overflow-auto bg-white" />
    </div>
  );
}
